import { useState, type ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Blocks,
  Image as ImageIcon,
  LayoutGrid,
  Lock,
  Minus,
  Package,
  Play,
  Star,
  Tag,
  Timer,
  PanelBottom,
  RectangleHorizontal,
  Trash2,
  TriangleAlert,
  Type,
  type LucideIcon,
} from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { ImageSourceInput } from './ImageSourceInput';
import { SaveAsSharedBlock, SharedBlockPicker } from './SharedBlockPicker';
import { ProductFeedFields } from './ProductFeedFields';
import { ShopProductPicker } from './ShopProductPicker';
import { Card } from './ui/Card';
import {
  BLOCK_LABELS,
  createBlock,
  showRuleError,
  MIN_SENDER_DETAILS_LENGTH,
  MISSING_SENDER_MESSAGE,
  type Block,
  type FooterDefaults,
} from '../lib/blocks';
import { blockSummary } from '../lib/preflightSteps';

function BlockFields({ block, onChange }: { block: Block; onChange: (next: Block) => void }) {
  switch (block.type) {
    case 'text':
      return (
        <textarea
          aria-label="Text content"
          className="w-full min-h-[90px] rounded-[9px] border border-zinc-300 px-3 py-2 text-sm font-[inherit]"
          value={block.text}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
        />
      );
    case 'image':
      return (
        <div className="flex flex-col gap-2">
          <ImageSourceInput
            id={`img-src-${block.id}`}
            label="Image URL"
            variant="card"
            value={block.src}
            onChange={(src) => onChange({ ...block, src })}
          />
          <Input
            aria-label="Alt text"
            placeholder="Alt text (description)"
            value={block.alt}
            onChange={(e) => onChange({ ...block, alt: e.target.value })}
          />
        </div>
      );
    case 'button':
      return (
        <div className="flex flex-col gap-2">
          <Input
            aria-label="Button text"
            placeholder="Button text"
            value={block.label}
            onChange={(e) => onChange({ ...block, label: e.target.value })}
          />
          <Input
            aria-label="Link"
            placeholder="https://"
            value={block.href}
            onChange={(e) => onChange({ ...block, href: e.target.value })}
          />
        </div>
      );
    case 'product':
      return (
        <div className="flex flex-col gap-2">
          <ShopProductPicker onPick={(fields) => onChange({ ...block, ...fields })} />
          <Input
            aria-label="Product name"
            placeholder="Product name"
            value={block.name}
            onChange={(e) => onChange({ ...block, name: e.target.value })}
          />
          <Input
            aria-label="Price"
            placeholder="Price"
            value={block.price}
            onChange={(e) => onChange({ ...block, price: e.target.value })}
          />
          <ImageSourceInput
            id={`img-product-${block.id}`}
            label="Image URL"
            variant="card"
            value={block.imageSrc}
            onChange={(imageSrc) => onChange({ ...block, imageSrc })}
          />
          <Input
            aria-label="Link"
            placeholder="https://"
            value={block.href}
            onChange={(e) => onChange({ ...block, href: e.target.value })}
          />
        </div>
      );
    case 'divider':
      return <div className="text-muted text-sm">Horizontal divider line.</div>;
    case 'countdown':
    case 'coupon':
    case 'reviews':
    case 'video':
    case 'shared':
    case 'products':
      return <CompactFields block={block} onChange={onChange} />;
    case 'footer':
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`footer-addr-${block.id}`}>Sender details</Label>
            <Input
              id={`footer-addr-${block.id}`}
              value={block.companyAddress}
              placeholder="Your brand name"
              onChange={(e) => onChange({ ...block, companyAddress: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`footer-legal-${block.id}`}>Legal notice link (optional)</Label>
            <Input
              id={`footer-legal-${block.id}`}
              value={block.legalNoticeHref ?? ''}
              placeholder="{{ legal_notice_url }}"
              onChange={(e) => onChange(withLegalNoticeHref(block, e.target.value))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`footer-unsub-${block.id}`}>Unsubscribe link</Label>
            <Input
              id={`footer-unsub-${block.id}`}
              value={block.unsubscribeHref}
              onChange={(e) => onChange({ ...block, unsubscribeHref: e.target.value })}
            />
          </div>
        </div>
      );
  }
}

export const BLOCK_ICONS: Record<Block['type'], LucideIcon> = {
  text: Type,
  image: ImageIcon,
  button: RectangleHorizontal,
  product: Package,
  divider: Minus,
  countdown: Timer,
  coupon: Tag,
  reviews: Star,
  video: Play,
  shared: Blocks,
  products: LayoutGrid,
  footer: PanelBottom,
};

/** Whether a block needs attention before sending (shown as a warning dot). */
export function blockWarning(block: Block): string | null {
  if (block.type === 'image' && !block.alt.trim()) {
    return 'Add alt text. It is read aloud by screen readers and shown when images are blocked.';
  }
  if (block.type === 'footer' && block.companyAddress.trim().length < MIN_SENDER_DETAILS_LENGTH) {
    return MISSING_SENDER_MESSAGE;
  }
  return null;
}

/** An emptied legal notice field removes the key, so the footer renders without the link. */
function withLegalNoticeHref(block: Extract<Block, { type: 'footer' }>, value: string): Block {
  const { legalNoticeHref: _old, ...rest } = block;
  return value === '' ? rest : { ...rest, legalNoticeHref: value };
}

function Field({
  id,
  label,
  children,
  hint,
}: {
  id: string;
  label: string;
  children: ReactNode;
  hint?: string | null | undefined;
}) {
  return (
    <div className="pf-field">
      <label className="pf-flabel" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint ? (
        <p className="pf-hint pf-hint-warn" id={`${id}-hint`}>
          <TriangleAlert aria-hidden="true" />
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** The same fields as BlockFields, with visible labels (same accessible names) for the row layout. */
function CompactFields({ block, onChange }: { block: Block; onChange: (next: Block) => void }) {
  const id = (f: string) => `blk-${block.id}-${f}`;
  const input = (f: string, value: string, set: (v: string) => void, warn?: string | null) => (
    <input
      id={id(f)}
      type="text"
      value={value}
      data-warn={warn ? 'true' : undefined}
      aria-describedby={warn ? `${id(f)}-hint` : undefined}
      onChange={(e) => set(e.target.value)}
    />
  );
  switch (block.type) {
    case 'text':
      return (
        <Field id={id('text')} label="Text content">
          <textarea
            id={id('text')}
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
          />
        </Field>
      );
    case 'image': {
      const warn = blockWarning(block);
      return (
        <>
          <Field id={id('src')} label="Image URL">
            <ImageSourceInput
              id={id('src')}
              label="Image URL"
              variant="row"
              value={block.src}
              onChange={(src) => onChange({ ...block, src })}
            />
          </Field>
          <Field id={id('alt')} label="Alt text" hint={warn}>
            {input('alt', block.alt, (alt) => onChange({ ...block, alt }), warn)}
          </Field>
        </>
      );
    }
    case 'button':
      return (
        <div className="pf-row2">
          <Field id={id('label')} label="Button text">
            {input('label', block.label, (label) => onChange({ ...block, label }))}
          </Field>
          <Field id={id('href')} label="Link">
            {input('href', block.href, (href) => onChange({ ...block, href }))}
          </Field>
        </div>
      );
    case 'product':
      return (
        <>
          <div className="pf-field">
            <ShopProductPicker onPick={(fields) => onChange({ ...block, ...fields })} />
          </div>
          <div className="pf-row2">
            <Field id={id('name')} label="Product name">
              {input('name', block.name, (name) => onChange({ ...block, name }))}
            </Field>
            <Field id={id('price')} label="Price">
              {input('price', block.price, (price) => onChange({ ...block, price }))}
            </Field>
          </div>
          <Field id={id('img')} label="Image URL">
            <ImageSourceInput
              id={id('img')}
              label="Image URL"
              variant="row"
              value={block.imageSrc}
              onChange={(imageSrc) => onChange({ ...block, imageSrc })}
            />
          </Field>
          <Field id={id('href')} label="Link">
            {input('href', block.href, (href) => onChange({ ...block, href }))}
          </Field>
        </>
      );
    case 'divider':
      return <p className="pf-hint pf-field">A thin horizontal line between blocks.</p>;
    case 'countdown':
      return (
        <div className="pf-row2">
          <Field id={id('heading')} label="Heading">
            {input('heading', block.heading, (heading) => onChange({ ...block, heading }))}
          </Field>
          <Field id={id('endsOn')} label="Offer ends (text)">
            {input('endsOn', block.endsOn, (endsOn) => onChange({ ...block, endsOn }))}
          </Field>
        </div>
      );
    case 'coupon':
      return (
        <>
          <Field id={id('heading')} label="Heading">
            {input('heading', block.heading, (heading) => onChange({ ...block, heading }))}
          </Field>
          <Field id={id('code')} label="Code">
            {input('code', block.code, (code) => onChange({ ...block, code }))}
          </Field>
          <Field id={id('note')} label="Note">
            {input('note', block.note, (note) => onChange({ ...block, note }))}
          </Field>
        </>
      );
    case 'reviews':
      return (
        <>
          <Field id={id('stars')} label="Stars (0 to 5)">
            {input('stars', String(block.stars), (v) =>
              onChange({ ...block, stars: Math.max(0, Math.min(5, Number(v) || 0)) }),
            )}
          </Field>
          <Field id={id('quote')} label="Quote">
            {input('quote', block.quote, (quote) => onChange({ ...block, quote }))}
          </Field>
          <Field id={id('source')} label="Source">
            {input('source', block.source, (source) => onChange({ ...block, source }))}
          </Field>
        </>
      );
    case 'video':
      return (
        <>
          <Field id={id('thumb')} label="Thumbnail URL">
            <ImageSourceInput
              id={id('thumb')}
              label="Thumbnail URL"
              variant="row"
              value={block.thumbnailSrc}
              onChange={(thumbnailSrc) => onChange({ ...block, thumbnailSrc })}
            />
          </Field>
          <Field id={id('alt')} label="Alt text">
            {input('alt', block.alt, (alt) => onChange({ ...block, alt }))}
          </Field>
          <Field id={id('href')} label="Video link">
            {input('href', block.href, (href) => onChange({ ...block, href }))}
          </Field>
        </>
      );
    case 'shared':
      return <SharedBlockPicker block={block} onChange={onChange} fieldId={id('pick')} />;
    case 'products':
      return <ProductFeedFields block={block} onChange={onChange} idPrefix={`blk-${block.id}`} />;
    case 'footer': {
      const warn = blockWarning(block);
      return (
        <>
          <p className="pf-lockline pf-field">
            <Lock aria-hidden="true" />
            Required in every campaign. It cannot be deleted.
          </p>
          <Field id={id('addr')} label="Sender details" hint={warn}>
            {input(
              'addr',
              block.companyAddress,
              (companyAddress) => onChange({ ...block, companyAddress }),
              warn,
            )}
          </Field>
          <Field id={id('legal')} label="Legal notice link (optional)">
            {input('legal', block.legalNoticeHref ?? '', (legalNoticeHref) =>
              onChange(withLegalNoticeHref(block, legalNoticeHref)),
            )}
          </Field>
          <Field id={id('unsub')} label="Unsubscribe link">
            {input('unsub', block.unsubscribeHref, (unsubscribeHref) =>
              onChange({ ...block, unsubscribeHref }),
            )}
          </Field>
        </>
      );
    }
  }
}

/** Show/hide rule and device choice for one block (K4). Empty = everyone, every device. */
function VisibilityFields({ block, onChange }: { block: Block; onChange: (next: Block) => void }) {
  if (block.type === 'footer') return null;
  const id = (f: string) => `blk-${block.id}-${f}`;
  const error = showRuleError(block);
  const setShow = (value: string) => {
    const { show: _old, ...rest } = block;
    onChange(value === '' ? (rest as Block) : { ...block, show: value });
  };
  return (
    <>
      <Field id={id('show')} label="Show only if (optional)" hint={error}>
        <input
          id={id('show')}
          type="text"
          value={block.show ?? ''}
          placeholder="language=de ordered=no"
          data-warn={error ? 'true' : undefined}
          aria-describedby={error ? `${id('show')}-hint` : undefined}
          onChange={(e) => setShow(e.target.value)}
        />
      </Field>
      <p className="pf-hint pf-field">
        Terms: language, country, segment, label, ordered=yes|no. Use | for alternatives, != to
        exclude. All terms must match.
      </p>
      <Field id={id('device')} label="Show on">
        <select
          id={id('device')}
          value={block.device ?? 'all'}
          onChange={(e) => {
            const device = e.target.value as 'all' | 'desktop' | 'mobile';
            const { device: _old, ...rest } = block;
            onChange(device === 'all' ? (rest as Block) : { ...block, device });
          }}
        >
          <option value="all">All devices</option>
          <option value="desktop">Desktop only</option>
          <option value="mobile">Mobile only</option>
        </select>
      </Field>
    </>
  );
}

function BlockRows({
  blocks,
  onChange,
  openBlockId,
  onOpenBlockChange,
  onHighlight,
  disabled,
  footerDefaults,
}: {
  blocks: Block[];
  onChange: (next: Block[]) => void;
  openBlockId?: string | null | undefined;
  onOpenBlockChange?: ((id: string | null) => void) | undefined;
  onHighlight?: ((id: string | null) => void) | undefined;
  disabled?: boolean | undefined;
  footerDefaults?: FooterDefaults | undefined;
}) {
  const [ownOpen, setOwnOpen] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const open = openBlockId === undefined ? ownOpen : openBlockId;
  const setOpen = (id: string | null) => {
    setOwnOpen(id);
    onOpenBlockChange?.(id);
  };
  const footerIndex = blocks.findIndex((b) => b.type === 'footer');
  const hasFooter = footerIndex >= 0;

  function swap(index: number, target: number) {
    const next = [...blocks];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    onChange(next);
  }

  function add(type: Block['type']) {
    const block = createBlock(type, footerDefaults);
    const next = [...blocks];
    // New blocks go above the footer, so the footer stays last.
    if (hasFooter && type !== 'footer') next.splice(footerIndex, 0, block);
    else next.push(block);
    onChange(next);
    setAdded(block.id);
    setOpen(block.id);
  }

  // Movable = every block except a footer; it never moves past the footer.
  const lastMovable = hasFooter ? footerIndex - 1 : blocks.length - 1;

  return (
    <div>
      <ul className="pf-blocks" aria-label="Blocks">
        {blocks.length === 0 ? (
          <li className="pf-blk">
            <p className="pf-hint" style={{ padding: '12px' }}>
              No blocks yet. Add a text block to start.
            </p>
          </li>
        ) : null}
        {blocks.map((block, index) => {
          const Icon = BLOCK_ICONS[block.type];
          const isOpen = open === block.id;
          const warn = blockWarning(block);
          const label = BLOCK_LABELS[block.type];
          const bodyId = `blk-${block.id}-body`;
          return (
            <li
              key={block.id}
              className={`pf-blk${isOpen ? ' pf-open' : ''}${added === block.id ? ' pf-blk-in' : ''}`}
              data-block={block.id}
              onMouseEnter={() => onHighlight?.(block.id)}
              onMouseLeave={() => onHighlight?.(null)}
            >
              <button
                type="button"
                className="pf-blk-head"
                aria-expanded={isOpen}
                aria-controls={bodyId}
                onClick={() => setOpen(isOpen ? null : block.id)}
                onFocus={() => onHighlight?.(block.id)}
                onBlur={() => onHighlight?.(null)}
              >
                <span className="pf-bico">
                  <Icon aria-hidden />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="pf-bt">{label}</span>
                  <span className="pf-bs">{blockSummary(block)}</span>
                </span>
                {warn ? (
                  <>
                    <span className="pf-bw" aria-hidden="true" />
                    <span className="pf-sr">, needs attention</span>
                  </>
                ) : (
                  <span />
                )}
              </button>
              <div className="pf-blk-body" id={bodyId} inert={!isOpen}>
                <div>
                  <fieldset className="pf-blk-ed m-0 border-0 min-w-0" disabled={disabled}>
                    <legend className="pf-sr">{label} block</legend>
                    <CompactFields
                      block={block}
                      onChange={(next) => {
                        const copy = [...blocks];
                        copy[index] = next;
                        onChange(copy);
                      }}
                    />
                    {block.type !== 'footer' ? (
                      <VisibilityFields
                        block={block}
                        onChange={(next) => {
                          const copy = [...blocks];
                          copy[index] = next;
                          onChange(copy);
                        }}
                      />
                    ) : null}
                    {block.type !== 'footer' ? (
                      <div className="pf-actions">
                        <button
                          type="button"
                          className="pf-btn pf-btn-sm pf-btn-ghost pf-btn-icon"
                          aria-label={`Move ${label} up`}
                          disabled={index === 0}
                          onClick={() => swap(index, index - 1)}
                        >
                          <ArrowUp aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="pf-btn pf-btn-sm pf-btn-ghost pf-btn-icon"
                          aria-label={`Move ${label} down`}
                          disabled={index >= lastMovable}
                          onClick={() => swap(index, index + 1)}
                        >
                          <ArrowDown aria-hidden="true" />
                        </button>
                        {block.type !== 'shared' ? (
                          <SaveAsSharedBlock
                            block={block}
                            onReplace={(next) => {
                              const copy = [...blocks];
                              copy[index] = next;
                              onChange(copy);
                            }}
                          />
                        ) : null}
                        <button
                          type="button"
                          className="pf-btn pf-btn-sm pf-btn-ghost pf-btn-danger"
                          style={{ marginLeft: 'auto' }}
                          aria-label={`Delete ${label}`}
                          onClick={() => {
                            if (isOpen) setOpen(null);
                            onChange(blocks.filter((_, i) => i !== index));
                          }}
                        >
                          <Trash2 aria-hidden="true" />
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </fieldset>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {!disabled ? (
        <div className="pf-addrow">
          <span className="pf-al">Add block</span>
          {(Object.keys(BLOCK_LABELS) as Block['type'][]).map((type) => {
            const Icon = BLOCK_ICONS[type];
            return (
              <button
                key={type}
                type="button"
                className="pf-chip"
                aria-label={`Add ${BLOCK_LABELS[type]}`}
                disabled={type === 'footer' && hasFooter}
                onClick={() => add(type)}
              >
                <Icon aria-hidden />
                {BLOCK_LABELS[type]}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function BlockEditor({
  blocks,
  onChange,
  variant = 'cards',
  openBlockId,
  onOpenBlockChange,
  onHighlight,
  disabled,
  footerDefaults,
}: {
  blocks: Block[];
  onChange: (next: Block[]) => void;
  /** `rows` = the collapsible row list used by the campaign editor (styles/preflight.css). */
  variant?: 'cards' | 'rows' | undefined;
  openBlockId?: string | null | undefined;
  onOpenBlockChange?: ((id: string | null) => void) | undefined;
  onHighlight?: ((id: string | null) => void) | undefined;
  disabled?: boolean | undefined;
  /** Brand kit defaults for a footer added here (see `useFooterDefaults`). */
  footerDefaults?: FooterDefaults | undefined;
}) {
  if (variant === 'rows') {
    return (
      <BlockRows
        blocks={blocks}
        onChange={onChange}
        openBlockId={openBlockId}
        onOpenBlockChange={onOpenBlockChange}
        onHighlight={onHighlight}
        disabled={disabled}
        footerDefaults={footerDefaults}
      />
    );
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    onChange(next);
  }

  function update(index: number, block: Block) {
    const next = [...blocks];
    next[index] = block;
    onChange(next);
  }

  function remove(index: number) {
    onChange(blocks.filter((_, i) => i !== index));
  }

  function add(type: Block['type']) {
    const block = createBlock(type, footerDefaults);
    const next = [...blocks];
    // New blocks go above the footer, so the footer stays last (same as BlockRows).
    const footerIndex = blocks.findIndex((b) => b.type === 'footer');
    if (footerIndex >= 0 && type !== 'footer') next.splice(footerIndex, 0, block);
    else next.push(block);
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3">
      <ol className="flex flex-col gap-3 list-none m-0 p-0">
        {blocks.map((block, index) => (
          <li key={block.id}>
            <Card className="p-3.5 flex flex-col gap-2.5">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{BLOCK_LABELS[block.type]}</span>
                <div className="ml-auto flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Move ${BLOCK_LABELS[block.type]} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp size={15} aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Move ${BLOCK_LABELS[block.type]} down`}
                    disabled={index === blocks.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown size={15} aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-danger-text"
                    aria-label={`Delete ${BLOCK_LABELS[block.type]}`}
                    onClick={() => remove(index)}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </Button>
                </div>
              </div>
              <BlockFields block={block} onChange={(next) => update(index, next)} />
            </Card>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(BLOCK_LABELS) as Block['type'][]).map((type) => (
          <Button key={type} variant="secondary" size="sm" onClick={() => add(type)}>
            + {BLOCK_LABELS[type]}
          </Button>
        ))}
      </div>
    </div>
  );
}
